import { FC } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'

import { borderRadiusDefault, colors } from '../../../../../themes/theme'
import { AppType } from '../../../../game/types/Game'
import { useShowSubgenreForGame } from '../../../../top-game/hooks/topGrossingGameHooks'
import { TrackedGame } from '../../../types/TrackedGame'
import { LiveEventsNotPublishedBadge } from '../../LiveEventsNotPublishedBadge/LiveEventsNotPublishedBadge'

type EventDialogGameLineProps = {
  trackedGame: TrackedGame
}
/**
 * The first line of the event dialog header: the game the event ran in as a
 * small bold link, its publisher muted after a dot, then its subgenre as a
 * pill when the user is entitled to see it. PC/console titles have no game
 * page, so their name is plain (same rule as `GameLink`).
 */
export const EventDialogGameLine: FC<EventDialogGameLineProps> = ({ trackedGame }) => {
  const { game } = trackedGame
  const showSubgenre = useShowSubgenreForGame(game)
  const hasGamePage = game.appType !== AppType.PC_CONSOLE
  const nameSx = { fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' } as const

  return (
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, minWidth: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}
    >
      {hasGamePage ? (
        <Link component={RouterLink} to={`/game/${game.id}`} underline="hover" sx={nameSx}>
          {game.resolvedName}
        </Link>
      ) : (
        <Typography component="span" sx={{ ...nameSx, color: 'text.primary' }}>
          {game.resolvedName}
        </Typography>
      )}
      {game.artist ? (
        <Typography component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1.3, color: colors.textMuted, whiteSpace: 'nowrap' }}>
          {'\u00b7 '}
          {game.artist}
        </Typography>
      ) : null}
      {showSubgenre && game.conventionalSubgenre ? (
        <>
          {/* Same dot as before the publisher, so the pill reads as one more
              item in the line rather than a tag floating after it. */}
          <Typography component="span" aria-hidden sx={{ fontSize: '0.8125rem', lineHeight: 1.3, color: colors.textMuted }}>
            {'\u00b7'}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '11px',
              lineHeight: 1.3,
              px: '9px',
              py: '2px',
              borderRadius: borderRadiusDefault,
              bgcolor: 'background.default',
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            {game.conventionalSubgenre}
          </Typography>
        </>
      ) : null}
      {!trackedGame.published && <LiveEventsNotPublishedBadge sx={{ display: 'inline-flex' }} />}
    </Box>
  )
}
